import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Phone,
  Mail,
  Shield,
  AlertCircle,
  CheckCircle,
  XCircle,
  X,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { adminAccountsApi, AdminAccount } from '../services/api/adminAccountsApi';
import { useAuth } from '../contexts/AuthContext';
import { useMasterAuth } from '../hooks/useMasterAuth';
import Button from './ui/Button';
import Input from './ui/Input';

const AdminManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { switchToAdmin } = useAuth();
  const { getCurrentMasterRole } = useMasterAuth();
  
  const [adminAccounts, setAdminAccounts] = useState<AdminAccount[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<AdminAccount[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [planFilter, setPlanFilter] = useState<'all' | 'basic' | 'premium' | 'enterprise'>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminAccount | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAdmin, setDeletingAdmin] = useState<AdminAccount | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  const [newAdmin, setNewAdmin] = useState({
    companyName: '',
    email: '',
    password: '',
    contactPerson: '',
    phone: '',
    address: '',
    website: '',
    description: '',
    logo: '',
    subscription: {
      plan: 'basic' as const,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      maxEvents: 20,
      maxTicketsPerEvent: 1000
    },
    settings: {
      currency: 'JOD',
      timezone: 'Asia/Amman',
      language: 'en' as const,
      allowOnlinePayments: true,
      requireApproval: false,
      emailNotifications: true
    },
    isActive: true
  });
  
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  useEffect(() => {
    loadAdminAccounts();
  }, []);

  useEffect(() => {
    filterAccounts();
  }, [adminAccounts, searchQuery, statusFilter, planFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAdminAccounts = async () => {
    setIsLoading(true);
    try {
      const response = await adminAccountsApi.getAll();
      setAdminAccounts(response.data || []);
    } catch (error: any) {
      console.error('Error loading accounts:', error);
      setNotification({
        type: 'error',
        message: error?.message || 'Failed to load accounts'
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const filterAccounts = () => {
    let filtered = adminAccounts;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(account =>
        account.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        account.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        account.contactPerson.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(account =>
        statusFilter === 'active' ? account.isActive : !account.isActive
      );
    }

    // Plan filter
    if (planFilter !== 'all') {
      filtered = filtered.filter(account => {
        const plan = (account as any).plan || (account as any).subscription?.plan;
        return plan?.toLowerCase() === planFilter;
      });
    }

    setFilteredAccounts(filtered);
  };

  const handleCreateAdmin = async () => {
    try {
      if (!newAdmin.companyName || !newAdmin.email || !newAdmin.password || !newAdmin.contactPerson || !newAdmin.phone || !newAdmin.address) {
        setNotification({
          type: 'error',
          message: 'Please fill in all required fields'
        });
        setTimeout(() => setNotification(null), 5000);
        return;
      }

      // Upload logo if selected
      let logoUrl = newAdmin.logo;
      if (logoFile) {
        const uploadedUrl = await uploadLogo();
        if (uploadedUrl) {
          logoUrl = uploadedUrl;
        }
      }

      const subscriptionEndDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const createdAccount = await adminAccountsApi.create({
        companyName: newAdmin.companyName,
        email: newAdmin.email,
        password: newAdmin.password,
        contactPerson: newAdmin.contactPerson,
        phone: newAdmin.phone,
        address: newAdmin.address,
        website: newAdmin.website || undefined,
        description: newAdmin.description || undefined,
        logo: logoUrl || undefined,
        plan: newAdmin.subscription.plan.toUpperCase() as 'BASIC' | 'PREMIUM' | 'ENTERPRISE',
        maxEvents: newAdmin.subscription.maxEvents,
        maxTicketsPerEvent: newAdmin.subscription.maxTicketsPerEvent,
        subscriptionEndDate
      });

      setShowCreateModal(false);
      resetForm();
      
      // Reload admin accounts to ensure logo is included
      await loadAdminAccounts();
      
      setNotification({
        type: 'success',
        message: `Account for "${createdAccount.companyName}" created successfully!`
      });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error creating account:', error);
      setNotification({
        type: 'error',
        message: error?.message || 'Error creating account. Email might already exist.'
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleEditAdmin = async () => {
    if (!editingAdmin) return;

    try {
      // Upload logo if selected
      let logoUrl = editingAdmin.logo;
      if (logoFile) {
        const uploadedUrl = await uploadLogo();
        if (uploadedUrl) {
          logoUrl = uploadedUrl;
        }
      }

      // Check if password should be updated (only for PLATFORM_OWNER)
      const masterRole = getCurrentMasterRole();
      const isPlatformOwner = masterRole === 'PLATFORM_OWNER';
      
      if (isPlatformOwner && newPassword) {
        if (newPassword.length < 8) {
          setNotification({
            type: 'error',
            message: 'Password must be at least 8 characters long'
          });
          setTimeout(() => setNotification(null), 5000);
          return;
        }
        
        if (newPassword !== confirmPassword) {
          setNotification({
            type: 'error',
            message: 'Passwords do not match'
          });
          setTimeout(() => setNotification(null), 5000);
          return;
        }
      }

      const plan = ((editingAdmin as any).plan || (editingAdmin as any).subscription?.plan || 'BASIC').toUpperCase();
      const updateData: any = {
        companyName: editingAdmin.companyName,
        contactPerson: editingAdmin.contactPerson,
        phone: editingAdmin.phone,
        address: editingAdmin.address,
        logo: logoUrl,
        website: editingAdmin.website,
        description: editingAdmin.description,
        isActive: editingAdmin.isActive,
        plan: plan as 'BASIC' | 'PREMIUM' | 'ENTERPRISE',
        maxEvents: (editingAdmin as any).subscription?.maxEvents || (editingAdmin as any).maxEvents || 20,
        maxTicketsPerEvent: (editingAdmin as any).subscription?.maxTicketsPerEvent || (editingAdmin as any).maxTicketsPerEvent || 1000,
        subscriptionEndDate: (editingAdmin as any).subscription?.endDate || (editingAdmin as any).subscriptionEndDate
      };

      // Add password only if provided and user is PLATFORM_OWNER
      if (isPlatformOwner && newPassword) {
        updateData.password = newPassword;
      }

      const updatedAccount = await adminAccountsApi.update(editingAdmin.id, updateData);

      setAdminAccounts(prev =>
        prev.map(account => account.id === editingAdmin.id ? { ...account, ...updatedAccount } as any : account)
      );
      setShowEditModal(false);
      setEditingAdmin(null);
      setLogoPreview('');
      setLogoFile(null);
      setNewPassword('');
      setConfirmPassword('');
      setNotification({
        type: 'success',
        message: `"${updatedAccount.companyName}" updated successfully!`
      });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error updating account:', error);
      setNotification({
        type: 'error',
        message: error?.message || 'Error updating account. Please try again.'
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!deletingAdmin) return;

    try {
      const companyName = deletingAdmin.companyName;
      await adminAccountsApi.delete(deletingAdmin.id);
      setAdminAccounts(prev => prev.filter(account => account.id !== deletingAdmin.id));
      setShowDeleteModal(false);
      setDeletingAdmin(null);
      setNotification({
        type: 'success',
        message: `"${companyName}" has been permanently deleted.`
      });
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setNotification({
        type: 'error',
        message: error?.message || 'Error deleting account. Please try again.'
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleToggleStatus = async (admin: AdminAccount) => {
    try {
      const updatedAccount = await adminAccountsApi.toggleStatus(admin.id, !admin.isActive);
      setAdminAccounts(prev =>
        prev.map(account => account.id === admin.id ? { ...account, ...updatedAccount } as any : account)
      );
      setNotification({
        type: 'info',
        message: `"${admin.companyName}" is now ${updatedAccount.isActive ? 'active' : 'inactive'}.`
      });
      setTimeout(() => setNotification(null), 3000);
    } catch (error: any) {
      console.error('Error toggling account status:', error);
      setNotification({
        type: 'error',
        message: error?.message || 'Error updating account status. Please try again.'
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleSwitchToAdmin = (admin: AdminAccount) => {
    try {
      switchToAdmin(admin.id);
      navigate('/admin');
    } catch (error) {
      console.error('Error switching to admin:', error);
    }
  };

  const resetForm = () => {
    setNewAdmin({
      companyName: '',
      email: '',
      password: '',
      contactPerson: '',
      phone: '',
      address: '',
      website: '',
      description: '',
      logo: '',
      subscription: {
        plan: 'basic',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        maxEvents: 20,
        maxTicketsPerEvent: 1000
      },
      settings: {
        currency: 'JOD',
        timezone: 'Asia/Amman',
        language: 'en',
        allowOnlinePayments: true,
        requireApproval: false,
        emailNotifications: true
      },
      isActive: true
    });
    setLogoPreview('');
    setLogoFile(null);
  };

  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setNotification({
        type: 'error',
        message: 'Please select an image file'
      });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setNotification({
        type: 'error',
        message: 'Image size must be less than 2MB'
      });
      setTimeout(() => setNotification(null), 5000);
      return;
    }

    setLogoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setLogoPreview(result);
    };
    reader.readAsDataURL(file);
  };

  const uploadLogo = async (): Promise<string | null> => {
    if (!logoFile) return null;

    try {
      setUploadingLogo(true);
      
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(logoFile);
      });

      const base64Data = await base64Promise;

      // Upload to API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/upload/admin-logo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('admin_token') || ''}`
        },
        body: JSON.stringify({
          fileData: base64Data,
          contentType: logoFile.type,
          companyName: newAdmin.companyName || 'admin'
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload logo');
      }

      return data.data.url;
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      setNotification({
        type: 'error',
        message: error?.message || 'Failed to upload logo'
      });
      setTimeout(() => setNotification(null), 5000);
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'basic': return 'text-blue-400 bg-blue-400/10';
      case 'premium': return 'text-yellow-400 bg-yellow-400/10';
      case 'enterprise': return 'text-green-400 bg-green-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'JOD'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 max-w-md p-4 rounded-xl shadow-2xl transition-all duration-300 ${
          notification.type === 'success' ? 'bg-green-500/90 border border-green-400/50' :
          notification.type === 'error' ? 'bg-red-500/90 border border-red-400/50' :
          'bg-blue-500/90 border border-blue-400/50'
        } backdrop-blur-sm`}>
          <div className="flex items-center space-x-3">
            {notification.type === 'success' && <CheckCircle size={20} className="text-white" />}
            {notification.type === 'error' && <XCircle size={20} className="text-white" />}
            {notification.type === 'info' && <AlertCircle size={20} className="text-white" />}
            <span className="text-white font-medium">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="text-white/70 hover:text-white"
            >
              <XCircle size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Clients Accounts</h2>
          <p className="text-white/60">Manage company client accounts and access their dashboards</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
        >
          <Plus size={20} />
          Create Account
        </Button>
      </div>

      {/* Filters */}
      <div className="glass-card p-6 rounded-3xl">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search companies, emails, or contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search size={20} />}
              className="bg-white/10 border-white/20 text-white placeholder-white/50"
            />
          </div>
          
          <div className="flex gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value as any)}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
            >
              <option value="all">All Plans</option>
              <option value="basic">Basic</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>
      </div>

      {/* Clients Accounts List */}
      <div className="glass-card rounded-3xl overflow-hidden">
        {/* Table Header */}
        <div className="bg-white/5 px-6 py-4 border-b border-white/10">
          <div className="grid grid-cols-12 gap-4 text-sm font-medium text-white/70">
            <div className="col-span-3">Company</div>
            <div className="col-span-2">Contact</div>
            <div className="col-span-2">Plan & Status</div>
            <div className="col-span-3">Statistics</div>
            <div className="col-span-2">Actions</div>
          </div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-white/10">
          {filteredAccounts.map((admin) => (
            <div key={admin.id} className="px-6 py-4 hover:bg-white/5 transition-colors duration-200">
              <div className="grid grid-cols-12 gap-4 items-center">
                {/* Company Info */}
                <div className="col-span-3">
                  <div className="flex items-center space-x-3">
                    {admin.logo ? (
                      <img
                        src={admin.logo}
                        alt={admin.companyName}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                        <Building2 size={18} className="text-white" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-white">{admin.companyName}</h3>
                      <p className="text-sm text-white/60">{admin.contactPerson}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="col-span-2">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-sm text-white/70">
                      <Mail size={12} />
                      <span className="truncate">{admin.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-white/70">
                      <Phone size={12} />
                      <span>{admin.phone}</span>
                    </div>
                  </div>
                </div>

                {/* Plan & Status */}
                <div className="col-span-2">
                  <div className="space-y-2">
                    <div className={`inline-flex px-2 py-1 rounded-lg text-xs font-medium ${getPlanColor(((admin as any).plan || (admin as any).subscription?.plan || 'basic').toLowerCase())}`}>
                      {((admin as any).plan || (admin as any).subscription?.plan || 'BASIC').toUpperCase()}
                    </div>
                    <div className="flex items-center space-x-2">
                      {admin.isActive ? (
                        <>
                          <CheckCircle size={14} className="text-green-400" />
                          <span className="text-sm text-green-400">Active</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={14} className="text-red-400" />
                          <span className="text-sm text-red-400">Inactive</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Statistics */}
                <div className="col-span-3">
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-lg font-bold text-white">{(admin as any).stats?.totalEvents || 0}</div>
                      <div className="text-xs text-white/60">Events</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-white">{(admin as any).stats?.totalTicketsSold || 0}</div>
                      <div className="text-xs text-white/60">Tickets</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-white">{formatCurrency((admin as any).stats?.totalRevenue || 0)}</div>
                      <div className="text-xs text-white/60">Revenue</div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="col-span-2">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleSwitchToAdmin(admin)}
                      className="px-3 py-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white text-sm rounded-lg transition-all duration-200 flex items-center space-x-1"
                    >
                      <Eye size={14} />
                      <span>Access</span>
                    </button>
                    
                    <button
                      onClick={() => {
                        setEditingAdmin(admin);
                        setLogoPreview(admin.logo || '');
                        setLogoFile(null);
                        setNewPassword('');
                        setConfirmPassword('');
                        setShowEditModal(true);
                      }}
                      className="p-2 glass-button rounded-lg hover:scale-105 transition-transform"
                    >
                      <Edit size={14} className="text-white/70 hover:text-white" />
                    </button>
                    
                    <button
                      onClick={() => handleToggleStatus(admin)}
                      className={`p-2 glass-button rounded-lg hover:scale-105 transition-transform ${admin.isActive ? 'text-yellow-400' : 'text-green-400'}`}
                    >
                      <Shield size={14} />
                    </button>
                    
                    <button
                      onClick={() => {
                        setDeletingAdmin(admin);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 glass-button rounded-lg hover:scale-105 transition-transform text-red-400 hover:text-red-300"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {filteredAccounts.length === 0 && (
        <div className="text-center py-12">
          <Building2 size={48} className="text-white/30 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No client accounts found</h3>
          <p className="text-white/60">Create your first client account to get started.</p>
        </div>
      )}

      {/* Create Admin Modal - Matching User Dialog Style */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="glass-card rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Create Client Account</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-white/60 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Company Name</label>
                  <input
                    type="text"
                    value={newAdmin.companyName}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, companyName: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Enter company name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Contact Person</label>
                  <input
                    type="text"
                    value={newAdmin.contactPerson}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, contactPerson: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Contact person name"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="company@example.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={newAdmin.phone}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="+962-6-123-4567"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                  placeholder="Account password"
                  required
                />
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Subscription Plan</label>
                <select
                  value={newAdmin.subscription.plan}
                  onChange={(e) => setNewAdmin(prev => ({
                    ...prev,
                    subscription: {
                      ...prev.subscription,
                      plan: e.target.value as any,
                      maxEvents: e.target.value === 'basic' ? 20 : e.target.value === 'premium' ? 50 : 100,
                      maxTicketsPerEvent: e.target.value === 'basic' ? 1000 : e.target.value === 'premium' ? 5000 : 10000
                    }
                  }))}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                >
                  <option value="basic" className="bg-white text-gray-900">Basic Plan</option>
                  <option value="premium" className="bg-white text-gray-900">Premium Plan</option>
                  <option value="enterprise" className="bg-white text-gray-900">Enterprise Plan</option>
                </select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Currency</label>
                  <select
                    value={newAdmin.settings.currency}
                    onChange={(e) => setNewAdmin(prev => ({
                      ...prev,
                      settings: { ...prev.settings, currency: e.target.value }
                    }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="JOD" className="bg-white text-gray-900">JOD</option>
                    <option value="USD" className="bg-white text-gray-900">USD</option>
                    <option value="EUR" className="bg-white text-gray-900">EUR</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Language</label>
                  <select
                    value={newAdmin.settings.language}
                    onChange={(e) => setNewAdmin(prev => ({
                      ...prev,
                      settings: { ...prev.settings, language: e.target.value as any }
                    }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="en" className="bg-white text-gray-900">English</option>
                    <option value="ar" className="bg-white text-gray-900">العربية</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Status</label>
                  <select
                    value={newAdmin.isActive ? 'active' : 'inactive'}
                    onChange={(e) => setNewAdmin(prev => ({
                      ...prev,
                      isActive: e.target.value === 'active'
                    }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="active" className="bg-white text-gray-900">Active</option>
                    <option value="inactive" className="bg-white text-gray-900">Inactive</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Address</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={newAdmin.address}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Street address"
                  />
                  <input
                    type="url"
                    value={newAdmin.website || ''}
                    onChange={(e) => setNewAdmin(prev => ({ ...prev, website: e.target.value }))}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Website (optional)"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Description</label>
                <textarea
                  value={newAdmin.description || ''}
                  onChange={(e) => setNewAdmin(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/20 backdrop-blur-sm text-white placeholder-white/60 focus-glass border border-white/20 resize-none h-24"
                  placeholder="Company description (optional)"
                />
              </div>
              
              {/* Logo Upload */}
              <div>
                <label className="block text-white font-medium mb-2">Company Logo</label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-24 h-24 object-cover rounded-xl border-2 border-white/20"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setLogoPreview('');
                          setLogoFile(null);
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-white/5 border-2 border-dashed border-white/20 rounded-xl flex items-center justify-center">
                      <ImageIcon size={32} className="text-white/30" />
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoSelect}
                        className="hidden"
                      />
                      <div className={`px-4 py-3 glass-button rounded-xl text-white font-medium hover:scale-105 transition-transform flex items-center gap-2 justify-center ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <Upload size={18} />
                        {uploadingLogo ? 'Uploading...' : logoFile ? 'Change Logo' : 'Upload Logo'}
                      </div>
                    </label>
                    <p className="text-xs text-white/50 mt-2">PNG, JPG, or WebP (max 2MB)</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="flex-1 px-6 py-3 glass rounded-xl text-white font-medium hover:scale-105 transition-transform"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAdmin}
                className="flex-1 glass-button px-6 py-3 rounded-xl text-white font-medium glow hover:scale-105 transition-transform"
              >
                <Plus size={20} className="mr-2" />
                {t('admin.createAccount', 'Create Client Account')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Admin Modal - Matching User Dialog Style */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="glass-card rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Edit Client Account</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAdmin(null);
                }}
                className="text-white/60 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
          
          {editingAdmin && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Company Name</label>
                  <input
                    type="text"
                    value={editingAdmin.companyName}
                    onChange={(e) => setEditingAdmin(prev => prev ? ({ ...prev, companyName: e.target.value }) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Enter company name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Contact Person</label>
                  <input
                    type="text"
                    value={editingAdmin.contactPerson}
                    onChange={(e) => setEditingAdmin(prev => prev ? ({ ...prev, contactPerson: e.target.value }) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Contact person name"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={editingAdmin.email}
                    onChange={(e) => setEditingAdmin(prev => prev ? ({ ...prev, email: e.target.value }) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="company@example.com"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={editingAdmin.phone}
                    onChange={(e) => setEditingAdmin(prev => prev ? ({ ...prev, phone: e.target.value }) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="+962-6-123-4567"
                  />
                </div>
              </div>
              
              {/* Password Change Section - Only for PLATFORM_OWNER */}
              {getCurrentMasterRole() === 'PLATFORM_OWNER' && (
                <div className="border-t border-white/10 pt-4 mt-4">
                  <h4 className="text-white font-semibold mb-4">Change Password</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white font-medium mb-2">New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                        placeholder="Leave empty to keep current password"
                      />
                      <p className="text-xs text-white/50 mt-1">Minimum 8 characters</p>
                    </div>
                    
                    <div>
                      <label className="block text-white font-medium mb-2">Confirm Password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-white font-medium mb-2">Subscription Plan</label>
                <select
                  value={((editingAdmin as any).plan || (editingAdmin as any).subscription?.plan || 'BASIC').toLowerCase()}
                  onChange={(e) => setEditingAdmin(prev => prev ? ({
                    ...prev,
                    plan: e.target.value.toUpperCase(),
                    subscription: {
                      ...(prev as any).subscription || {},
                      plan: e.target.value as any,
                      maxEvents: e.target.value === 'basic' ? 20 : e.target.value === 'premium' ? 50 : 100,
                      maxTicketsPerEvent: e.target.value === 'basic' ? 1000 : e.target.value === 'premium' ? 5000 : 10000
                    }
                  } as any) : null)}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                >
                  <option value="basic" className="bg-white text-gray-900">Basic Plan</option>
                  <option value="premium" className="bg-white text-gray-900">Premium Plan</option>
                  <option value="enterprise" className="bg-white text-gray-900">Enterprise Plan</option>
                </select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Currency</label>
                  <select
                    value={(editingAdmin as any).currency || 'JOD'}
                    onChange={(e) => setEditingAdmin(prev => prev ? ({
                      ...prev,
                      currency: e.target.value
                    } as any) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="JOD" className="bg-white text-gray-900">JOD</option>
                    <option value="USD" className="bg-white text-gray-900">USD</option>
                    <option value="EUR" className="bg-white text-gray-900">EUR</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Language</label>
                  <select
                    value={(editingAdmin as any).language || 'en'}
                    onChange={(e) => setEditingAdmin(prev => prev ? ({
                      ...prev,
                      language: e.target.value
                    } as any) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="en" className="bg-white text-gray-900">English</option>
                    <option value="ar" className="bg-white text-gray-900">العربية</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Status</label>
                  <select
                    value={editingAdmin.isActive ? 'active' : 'inactive'}
                    onChange={(e) => setEditingAdmin(prev => prev ? ({
                      ...prev,
                      isActive: e.target.value === 'active'
                    }) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200 focus-glass"
                  >
                    <option value="active" className="bg-white text-gray-900">Active</option>
                    <option value="inactive" className="bg-white text-gray-900">Inactive</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Address</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={editingAdmin.address}
                    onChange={(e) => setEditingAdmin(prev => prev ? ({ ...prev, address: e.target.value }) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Street address"
                  />
                  <input
                    type="url"
                    value={editingAdmin.website || ''}
                    onChange={(e) => setEditingAdmin(prev => prev ? ({ ...prev, website: e.target.value }) : null)}
                    className="w-full px-4 py-3 glass-input rounded-xl bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus-glass border border-gray-200"
                    placeholder="Website (optional)"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white font-medium mb-2">Description</label>
                <textarea
                  value={editingAdmin.description || ''}
                  onChange={(e) => setEditingAdmin(prev => prev ? ({ ...prev, description: e.target.value }) : null)}
                  className="w-full px-4 py-3 glass-input rounded-xl bg-white/20 backdrop-blur-sm text-white placeholder-white/60 focus-glass border border-white/20 resize-none h-24"
                  placeholder="Company description (optional)"
                />
              </div>
              
              {/* Logo Upload */}
              <div>
                <label className="block text-white font-medium mb-2">Company Logo</label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-24 h-24 object-cover rounded-xl border-2 border-white/20"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setLogoPreview('');
                          setLogoFile(null);
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-white/5 border-2 border-dashed border-white/20 rounded-xl flex items-center justify-center">
                      <ImageIcon size={32} className="text-white/30" />
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoSelect}
                        className="hidden"
                      />
                      <div className={`px-4 py-3 glass-button rounded-xl text-white font-medium hover:scale-105 transition-transform flex items-center gap-2 justify-center ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <Upload size={18} />
                        {uploadingLogo ? 'Uploading...' : logoFile ? 'Change Logo' : editingAdmin.logo ? 'Change Logo' : 'Upload Logo'}
                      </div>
                    </label>
                    <p className="text-xs text-white/50 mt-2">PNG, JPG, or WebP (max 2MB)</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex space-x-4 mt-8">
            <button
              onClick={() => {
                setShowEditModal(false);
                setEditingAdmin(null);
              }}
              className="flex-1 px-6 py-3 glass rounded-xl text-white font-medium hover:scale-105 transition-transform"
            >
              Cancel
            </button>
            <button
              onClick={handleEditAdmin}
              disabled={!editingAdmin?.companyName || !editingAdmin?.email || !editingAdmin?.contactPerson}
              className="flex-1 glass-button px-6 py-3 rounded-xl text-white font-medium glow hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Edit size={20} className="mr-2" />
              {t('admin.updateAccount', 'Update Client Account')}
            </button>
          </div>
        </div>
      </div>
    )}

      {/* Delete Confirmation Modal - Matching User Dialog Style */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="glass-card rounded-3xl p-8 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Delete Client Account</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingAdmin(null);
                }}
                className="text-white/60 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={32} className="text-white" />
              </div>
              <p className="text-white/70 mb-2">
                Are you sure you want to delete <span className="font-semibold text-white">"{deletingAdmin?.companyName}"</span>?
              </p>
              <p className="text-white/60 text-sm mb-4">
                This action cannot be undone.
              </p>
              
              {deletingAdmin && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                  <div className="flex items-center justify-center space-x-4 text-sm">
                    <div className="text-white/70">
                      <strong className="text-red-400">{(deletingAdmin as any).stats?.totalEvents || 0}</strong> Events
                    </div>
                    <div className="text-white/70">
                      <strong className="text-red-400">{(deletingAdmin as any).stats?.totalTicketsSold || 0}</strong> Tickets Sold
                    </div>
                    <div className="text-white/70">
                      <strong className="text-red-400">{formatCurrency((deletingAdmin as any).stats?.totalRevenue || 0)}</strong> Revenue
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingAdmin(null);
                }}
                className="flex-1 px-6 py-3 glass rounded-xl text-white font-medium hover:scale-105 transition-transform"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAdmin}
                className="flex-1 glass-button px-6 py-3 rounded-xl text-white font-medium glow hover:scale-105 transition-transform bg-gradient-to-r from-red-500 to-red-600"
              >
                <Trash2 size={20} className="mr-2" />
                {t('common.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement; 